class NewFaq extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;

    this.overview = this.querySelector('[data-faq-overview]');
    this.detail = this.querySelector('[data-faq-detail]');
    this.detailTitle = this.querySelector('[data-faq-detail-title]');
    this.resultSummary = this.querySelector('[data-faq-result-summary]');
    this.emptyState = this.querySelector('[data-faq-empty]');
    this.cta = this.querySelector('[data-faq-cta]');
    this.searchForm = this.querySelector('[data-faq-search-form]');
    this.searchInput = this.querySelector('[data-faq-search]');
    this.questions = Array.from(this.querySelectorAll('[data-faq-question]'));

    this.searchResultsLabel = this.dataset.searchResultsLabel || 'Search results';
    this.noResultsLabel = this.dataset.noResultsLabel || 'No answers found. Try a different search.';

    this.onClick = this.onClick.bind(this);
    this.onSubmit = this.onSubmit.bind(this);
    this.onInput = this.onInput.bind(this);
    this.onSearchKeydown = this.onSearchKeydown.bind(this);
    this.onHashChange = this.onHashChange.bind(this);
    this.onBlockSelect = this.onBlockSelect.bind(this);

    this.addEventListener('click', this.onClick);
    this.searchForm?.addEventListener('submit', this.onSubmit);
    this.searchInput?.addEventListener('input', this.onInput);
    this.searchInput?.addEventListener('keydown', this.onSearchKeydown);
    window.addEventListener('hashchange', this.onHashChange);

    if (window.Shopify?.designMode) {
      document.addEventListener('shopify:block:select', this.onBlockSelect);
    }

    this.onHashChange();
  }

  disconnectedCallback() {
    window.removeEventListener('hashchange', this.onHashChange);
    document.removeEventListener('shopify:block:select', this.onBlockSelect);
  }

  onClick(event) {
    const questionButton = event.target.closest('[data-faq-question-button]');
    if (questionButton && this.contains(questionButton)) {
      this.toggleQuestion(questionButton);
      return;
    }

    const topicButton = event.target.closest('[data-faq-topic]');
    if (topicButton && this.contains(topicButton)) {
      this.showTopic(topicButton.dataset.faqTopic, topicButton.dataset.faqTopicTitle || topicButton.textContent.trim());
      return;
    }

    const searchButton = event.target.closest('[data-faq-search-query]');
    if (searchButton && this.contains(searchButton)) {
      const query = searchButton.dataset.faqSearchQuery || '';
      if (this.searchInput) this.searchInput.value = query;
      this.showSearch(query, { scroll: true });
      return;
    }

    const backButton = event.target.closest('[data-faq-back]');
    if (backButton && this.contains(backButton)) {
      this.showOverview({ updateUrl: true, focusSearch: false });
      return;
    }

    const chatButton = event.target.closest('[data-faq-chat]');
    if (chatButton && this.contains(chatButton)) {
      const globalChatTrigger = document.querySelector('.chatTrigger');
      const chatPanel = document.querySelector('#aiChat');
      if (globalChatTrigger) {
        globalChatTrigger.click();
      } else if (chatPanel) {
        chatPanel.classList.add('active');
      }
    }
  }

  onSubmit(event) {
    event.preventDefault();
    const query = this.searchInput?.value.trim() || '';
    if (!query) {
      this.showOverview({ updateUrl: false, focusSearch: true });
      return;
    }

    this.showSearch(query, { scroll: true });
  }

  onInput() {
    const query = this.searchInput?.value.trim() || '';
    if (!query && this.classList.contains('is-search-view')) {
      this.showOverview({ updateUrl: false, focusSearch: true });
    }
  }

  onSearchKeydown(event) {
    if (event.key === 'Enter') {
      event.preventDefault();
      const query = this.searchInput?.value.trim() || '';
      if (query) {
        this.showSearch(query, { scroll: true });
      } else {
        this.showOverview({ updateUrl: false, focusSearch: true });
      }
      return;
    }

    if (event.key === 'Escape') {
      this.showOverview({ updateUrl: false, focusSearch: true });
    }
  }

  onHashChange() {
    const match = window.location.hash.match(/^#faq-topic=([^&]+)/);
    if (!match) {
      if (this.classList.contains('is-topic-view')) {
        this.showOverview({ updateUrl: false, focusSearch: false });
      }
      return;
    }

    const topic = decodeURIComponent(match[1]);
    const topicTrigger = this.querySelector(`[data-faq-topic="${CSS.escape(topic)}"]`);
    if (!topicTrigger) return;

    this.showTopic(topic, topicTrigger.dataset.faqTopicTitle || topicTrigger.textContent.trim(), {
      updateUrl: false,
      scroll: false
    });
  }

  onBlockSelect(event) {
    const question = event.target.closest('[data-faq-question]');
    if (!question || !this.contains(question)) return;

    const topic = question.dataset.topic;
    const topicTrigger = this.querySelector(`[data-faq-topic="${CSS.escape(topic)}"]`);
    this.showTopic(topic, topicTrigger?.dataset.faqTopicTitle || 'FAQ', { updateUrl: false });

    const button = question.querySelector('[data-faq-question-button]');
    if (button?.getAttribute('aria-expanded') !== 'true') this.toggleQuestion(button);
    question.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }

  showTopic(topic, title, options = {}) {
    const { updateUrl = true, scroll = true } = options;
    this.classList.add('is-topic-view');
    this.classList.remove('is-search-view');
    this.detail.hidden = false;
    this.cta.hidden = false;
    this.detailTitle.textContent = title;

    const resultCount = this.filterQuestions((question) => question.dataset.topic === topic);
    this.resultSummary.textContent = resultCount === 1 ? '1 answer' : `${resultCount} answers`;
    this.setEmptyState(resultCount === 0);

    if (updateUrl) {
      const url = `${window.location.pathname}${window.location.search}#faq-topic=${encodeURIComponent(topic)}`;
      window.history.pushState({ faqTopic: topic }, '', url);
    }

    if (scroll) this.scrollToMain();
  }

  showSearch(query, options = {}) {
    const { scroll = false } = options;
    const normalizedQuery = this.normalize(query);
    this.classList.remove('is-topic-view');
    this.classList.add('is-search-view');
    this.detail.hidden = false;
    this.cta.hidden = false;
    this.detailTitle.textContent = this.searchResultsLabel;

    const resultCount = this.filterQuestions((question) => this.getSearchScore(question, normalizedQuery));
    this.openVisibleQuestions();

    this.resultSummary.textContent = resultCount
      ? `${resultCount} ${resultCount === 1 ? 'answer' : 'answers'} for “${query}”`
      : '';
    this.setEmptyState(resultCount === 0);

    if (scroll) this.scrollToMain();
  }

  showOverview(options = {}) {
    const { updateUrl = false, focusSearch = false } = options;
    this.classList.remove('is-topic-view', 'is-search-view');
    this.detail.hidden = true;
    this.cta.hidden = true;
    this.closeAllQuestions();

    if (this.searchInput) this.searchInput.value = '';

    if (updateUrl) {
      window.history.pushState({}, '', `${window.location.pathname}${window.location.search}`);
      this.overview?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }

    if (focusSearch) this.searchInput?.focus();
  }

  filterQuestions(predicate) {
    this.closeAllQuestions();
    let count = 0;
    this.questions.forEach((question) => {
      const result = predicate(question);
      const isRankedResult = typeof result === 'number';
      const isVisible = isRankedResult ? result > 0 : Boolean(result);
      question.hidden = !isVisible;
      question.style.order = isRankedResult && isVisible ? String(-Math.round(result * 100)) : '';
      if (isVisible) count += 1;
    });
    return count;
  }

  setEmptyState(isEmpty) {
    this.emptyState.hidden = !isEmpty;
    if (isEmpty) this.emptyState.querySelector('p').textContent = this.noResultsLabel;
  }

  toggleQuestion(button) {
    if (!button) return;
    const answer = this.querySelector(`#${CSS.escape(button.getAttribute('aria-controls'))}`);
    if (!answer) return;

    const isOpen = button.getAttribute('aria-expanded') === 'true';
    button.setAttribute('aria-expanded', String(!isOpen));
    answer.hidden = isOpen;
  }

  openVisibleQuestions() {
    this.questions.forEach((question) => {
      if (question.hidden) return;
      const button = question.querySelector('[data-faq-question-button]');
      const answer = question.querySelector('[data-faq-answer]');
      button?.setAttribute('aria-expanded', 'true');
      if (answer) answer.hidden = false;
    });
  }

  closeAllQuestions() {
    this.questions.forEach((question) => {
      const button = question.querySelector('[data-faq-question-button]');
      const answer = question.querySelector('[data-faq-answer]');
      button?.setAttribute('aria-expanded', 'false');
      if (answer) answer.hidden = true;
    });
  }

  scrollToMain() {
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.detail.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'start' });
  }

  normalize(value) {
    return String(value || '')
      .toLocaleLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/&/g, ' and ')
      .replace(/[^a-z0-9]+/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  }

  getSearchScore(question, normalizedQuery) {
    if (!normalizedQuery) return 0;

    const searchableText = this.normalize(question.dataset.searchText || question.textContent);
    const questionText = this.normalize(question.dataset.questionText || '');
    const topicText = this.normalize(question.dataset.topic || '');
    let score = searchableText.includes(normalizedQuery) ? 100 : 0;
    const queryTokens = this.getMeaningfulTokens(normalizedQuery);
    const textTokens = searchableText.split(' ').filter(Boolean);
    const questionTokens = questionText.split(' ').filter(Boolean);
    const topicTokens = topicText.split(' ').filter(Boolean);

    if (questionText.includes(normalizedQuery)) score += 60;

    if (!queryTokens.length) return score;

    for (const queryToken of queryTokens) {
      let tokenScore = 0;
      for (const textToken of textTokens) {
        tokenScore = Math.max(tokenScore, this.getTokenMatchScore(queryToken, textToken));
        if (tokenScore === 12) break;
      }

      if (!tokenScore) return 0;
      score += tokenScore;

      const questionBoost = questionTokens.reduce(
        (best, token) => Math.max(best, this.getTokenMatchScore(queryToken, token)),
        0
      );
      const topicBoost = topicTokens.reduce(
        (best, token) => Math.max(best, this.getTokenMatchScore(queryToken, token)),
        0
      );
      score += questionBoost * 2 + topicBoost * 3;
    }

    return score;
  }

  getMeaningfulTokens(value) {
    const stopWords = new Set([
      'a', 'an', 'and', 'are', 'can', 'do', 'does', 'for', 'how', 'i', 'in', 'is', 'my',
      'of', 'on', 'or', 'the', 'to', 'what', 'when', 'where', 'which', 'why', 'with'
    ]);
    const tokens = value.split(' ').filter(Boolean);
    const meaningfulTokens = tokens.filter((token) => !stopWords.has(token));
    return meaningfulTokens.length ? meaningfulTokens : tokens;
  }

  getTokenMatchScore(queryToken, textToken) {
    if (queryToken === textToken) return 12;

    const queryCanonical = this.canonicalizeToken(queryToken);
    const textCanonical = this.canonicalizeToken(textToken);
    if (queryCanonical === textCanonical) return 11;

    if (queryToken.length >= 3 && textToken.startsWith(queryToken)) {
      return 8;
    }

    if (
      textToken.length >= 4 &&
      queryToken.startsWith(textToken) &&
      queryToken.length - textToken.length <= 2
    ) {
      return 8;
    }

    const maxDistance = queryToken.length >= 7 ? 2 : queryToken.length >= 4 ? 1 : 0;
    if (!maxDistance || Math.abs(queryToken.length - textToken.length) > maxDistance) return 0;

    const distance = this.levenshteinDistance(queryToken, textToken, maxDistance);
    return distance <= maxDistance ? 7 - distance : 0;
  }

  canonicalizeToken(token) {
    const aliases = {
      application: 'app',
      apps: 'app',
      deliver: 'shipping',
      delivered: 'shipping',
      deliveries: 'shipping',
      delivery: 'shipping',
      guarantee: 'warranty',
      guarantees: 'warranty',
      refund: 'return',
      refunded: 'return',
      refunds: 'return',
      replacement: 'replacement',
      returned: 'return',
      returns: 'return',
      ship: 'shipping',
      shipment: 'shipping',
      shipments: 'shipping',
      shipped: 'shipping',
      shipping: 'shipping',
      spare: 'replacement',
      spares: 'replacement',
      slacklines: 'slackline'
    };

    if (aliases[token]) return aliases[token];
    if (token.length >= 4) {
      const fuzzyAlias = Object.keys(aliases).find((alias) => {
        return Math.abs(alias.length - token.length) <= 1 && this.levenshteinDistance(token, alias, 1) <= 1;
      });
      if (fuzzyAlias) return aliases[fuzzyAlias];
    }
    if (token.length > 4 && token.endsWith('ies')) return `${token.slice(0, -3)}y`;
    if (token.length > 4 && token.endsWith('s') && !token.endsWith('ss')) return token.slice(0, -1);
    return token;
  }

  levenshteinDistance(source, target, limit) {
    let previous = Array.from({ length: target.length + 1 }, (_, index) => index);

    for (let sourceIndex = 1; sourceIndex <= source.length; sourceIndex += 1) {
      const current = [sourceIndex];
      let rowMinimum = current[0];

      for (let targetIndex = 1; targetIndex <= target.length; targetIndex += 1) {
        const substitutionCost = source[sourceIndex - 1] === target[targetIndex - 1] ? 0 : 1;
        const value = Math.min(
          current[targetIndex - 1] + 1,
          previous[targetIndex] + 1,
          previous[targetIndex - 1] + substitutionCost
        );
        current[targetIndex] = value;
        rowMinimum = Math.min(rowMinimum, value);
      }

      if (rowMinimum > limit) return limit + 1;
      previous = current;
    }

    return previous[target.length];
  }
}

if (!customElements.get('new-faq')) {
  customElements.define('new-faq', NewFaq);
}
